import {
  type NoveltyCalcErrorGroup,
  NoveltyCalcLaunchStatusFilter,
  type NoveltyCalcRequest,
  type NoveltyCalcResponse,
} from '@/api';
import priceCalculationEmptyState from '@/assets/price-calculation-empty-state.svg';
import type { DefaultOptionType } from '@/components/combobox/combobox';
import { FullSizeSpinner } from '@/components/full-size-spinner';
import { PageWrapper } from '@/components/page-wrapper/page-wrapper';
import { ResponsiveEmptyStateIllustration } from '@/components/responsive-empty-state-illustration';
import { useErrorNotification } from '@/hooks/use-error-notification';
import { normalizeApiError } from '@/utils/error-utils';
import { Button, Modal } from '@lego/connect-components-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '../../../routes';
import PriceCalcSetupPanel from './price-calc-setup-panel';
import PriceCalcSummaryPanel from './price-calc-summary-panel';
import {
  useCalculatePrices,
  useDiscardCalculation,
  useRerunCalculatePrices,
  useSaveCalculation,
} from './price-calculator-queries';
import PriceCalcSummaryTable from './summary-table/price-calc-summary-table';

export const PriceCalculatorPage = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const nextYear = currentYear + 1;

  const { mutate: calculatePrices, isPending } = useCalculatePrices();
  const { mutate: rerunCalculatePrices, isPending: isRerunPending } =
    useRerunCalculatePrices();
  const { mutateAsync: saveCalculation } = useSaveCalculation();
  const { mutate: discardCalculation } = useDiscardCalculation();
  const showErrorNotification = useErrorNotification();
  const leaveSummaryTriggerRef = useRef<HTMLElement | null>(null);
  const hasSavedSummaryRef = useRef(false);
  const rerunSimulationIdRef = useRef<number | null>(null);

  const [mode, setMode] = useState<'setup' | 'summary'>('setup');
  const [showLeaveSummaryModal, setShowLeaveSummaryModal] = useState(false);
  const [selectedPriceUnits, setSelectedPriceUnits] = useState<
    DefaultOptionType[]
  >([]);

  const [form, setForm] = useState<NoveltyCalcRequest>({
    planningYear: nextYear,
    priceUnitId: 0,
    launchStatusFilter: NoveltyCalcLaunchStatusFilter.All,
    overwritePreliminaryPrices: false,
  });

  const [simulationName, setSimulationName] = useState<string>(
    `${selectedPriceUnits[0]?.label} - ${form.planningYear} - ${form.launchStatusFilter}`,
  );

  const [calculatePriceResponse, setCalculatePriceResponse] =
    useState<NoveltyCalcResponse | null>(null);
  const [priceUnitHelpText, setPriceUnitHelpText] = useState<string | null>(
    null,
  );
  const [noveltyCalcErrors, setNoveltyCalcErrors] = useState<
    NoveltyCalcErrorGroup[] | null
  >(null);

  const clearErrors = () => {
    setPriceUnitHelpText(null);
    setNoveltyCalcErrors(null);
  };

  const handleSubmit = () => {
    clearErrors();
    setSimulationName(
      `${selectedPriceUnits[0]?.label} - ${form.planningYear} - ${form.launchStatusFilter}`,
    );
    calculatePrices(
      {
        noveltyCalcRequest: {
          ...form,
          priceUnitId: Number(selectedPriceUnits[0]?.id),
        },
      },
      {
        onSuccess: (response) => {
          if (
            response.errors.length > 0 &&
            !response.resultsSummary &&
            !response.simulationId
          ) {
            setCalculatePriceResponse(null);
            setNoveltyCalcErrors(response.errors);
            setMode('setup');
            return;
          }

          hasSavedSummaryRef.current = false;
          setCalculatePriceResponse(response);
          setMode('summary');
        },
        onError: (error) => {
          const normalizedError = normalizeApiError(error);

          if (normalizedError.kind === 'validation') {
            const firstValidationError = Object.values(
              normalizedError.fieldErrors,
            ).find((messages) => messages.length > 0)?.[0];

            if (firstValidationError) {
              setCalculatePriceResponse(null);
              setPriceUnitHelpText(firstValidationError);
              setMode('setup');
            }
          }
        },
      },
    );
  };

  const handleRerun = (lpIndexOverride: number) => {
    const simulationId = calculatePriceResponse?.simulationId;
    const priceUnitId = selectedPriceUnits[0]?.id;
    if (simulationId === null || simulationId === undefined || !priceUnitId) {
      return;
    }
    rerunSimulationIdRef.current = simulationId;
    rerunCalculatePrices(
      {
        simulationId,
        noveltyCalcRequest: {
          ...form,
          priceUnitId: Number(priceUnitId),
          lpIndexOverride,
        },
      },
      {
        onSuccess: (rerunResponse) => {
          // The user left this summary while the rerun was running: drop the result.
          if (rerunSimulationIdRef.current !== simulationId) {
            if (rerunResponse.simulationId) {
              discardCalculation({
                request: { simulationId: rerunResponse.simulationId },
              });
            }
            return;
          }
          rerunSimulationIdRef.current = null;

          if (
            rerunResponse.errors.length > 0 &&
            !rerunResponse.resultsSummary
          ) {
            discardCalculation({ request: { simulationId } });
            setShowLeaveSummaryModal(false);
            setCalculatePriceResponse(null);
            setNoveltyCalcErrors(rerunResponse.errors);
            setMode('setup');
            return;
          }
          setCalculatePriceResponse(rerunResponse);
        },
        onError: (error) => {
          if (rerunSimulationIdRef.current !== simulationId) return;
          rerunSimulationIdRef.current = null;
          showErrorNotification(normalizeApiError(error), 'rerun calculation');
        },
      },
    );
  };

  const handleLeaveSummary = () => {
    rerunSimulationIdRef.current = null;
    if (calculatePriceResponse?.simulationId) {
      discardCalculation({
        request: {
          simulationId: calculatePriceResponse.simulationId,
        },
      });
    }
    clearErrors();
    setShowLeaveSummaryModal(false);
    setMode('setup');
  };

  const hasUnsavedSummary = mode === 'summary' && !!calculatePriceResponse;

  // on browser close/refresh, show the unsaved changes modal
  // if user navigates away from the page, discard the calculation
  useEffect(() => {
    if (!hasUnsavedSummary) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // some browsers require setting returnValue to show the native leave-page confirmation dialog.
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    const handlePageHide = (event: PageTransitionEvent) => {
      if (
        event.persisted ||
        hasSavedSummaryRef.current ||
        !calculatePriceResponse.simulationId
      ) {
        return;
      }

      discardCalculation({
        request: {
          simulationId: calculatePriceResponse.simulationId,
        },
        initOverrides: { keepalive: true },
      });
    };

    window.addEventListener('pagehide', handlePageHide);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [calculatePriceResponse, discardCalculation, hasUnsavedSummary]);

  return (
    <PageWrapper
      headerProps={{
        title: 'Price Calculator',
      }}
      layout="fill"
      contentPaddingX="0px"
      maxWidth="100%"
    >
      <div
        style={{
          display: 'flex',
          height: '100%',
        }}
      >
        {mode === 'setup' ? (
          <>
            <PriceCalcSetupPanel
              form={form}
              setForm={setForm}
              setSelectedPriceUnits={setSelectedPriceUnits}
              selectedPriceUnits={selectedPriceUnits}
              priceUnitHelpText={priceUnitHelpText}
              onClearErrors={clearErrors}
              noveltyCalcErrors={noveltyCalcErrors}
              onSubmit={handleSubmit}
              isLoading={isPending}
            />
            <div
              style={{
                display: 'flex',
                flex: '1 1 0',
                height: '100%',
                minHeight: 0,
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {isPending ? (
                <FullSizeSpinner />
              ) : (
                <ResponsiveEmptyStateIllustration
                  src={priceCalculationEmptyState}
                  alt="Price calculation empty state"
                  maxWidth={1492}
                  alignment="left"
                />
              )}
            </div>
          </>
        ) : (
          <>
            <PriceCalcSummaryPanel
              form={form}
              selectedPriceUnits={selectedPriceUnits}
              onBackArrowClick={() => setShowLeaveSummaryModal(true)}
            />
            {calculatePriceResponse && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  width: '100%',
                  padding: '24px',
                  gap: '24px',
                }}
              >
                {isRerunPending ? (
                  <FullSizeSpinner type="full-container" />
                ) : (
                  <>
                    <PriceCalcSummaryTable
                      simulationName={simulationName}
                      onSimulationNameChange={setSimulationName}
                      calculatePriceResponse={calculatePriceResponse}
                      onRerun={handleRerun}
                    />
                    <Button
                      disabled={!calculatePriceResponse.simulationId}
                      onClick={async () => {
                        if (calculatePriceResponse.simulationId) {
                          await saveCalculation({
                            simulationId: calculatePriceResponse.simulationId,
                            saveNoveltyCalcRequestBody: {
                              simulationName,
                            },
                          });
                          hasSavedSummaryRef.current = true;
                          navigate(
                            `${ROUTES.SimulationTool.staticPath}/${calculatePriceResponse.simulationId}`,
                          );
                        }
                      }}
                    >
                      Create new simulation
                    </Button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>
      <Modal
        isOpen={showLeaveSummaryModal}
        triggerRef={leaveSummaryTriggerRef}
        onClose={() => setShowLeaveSummaryModal(false)}
        size={'small'}
      >
        <Modal.Title>Leave without saving?</Modal.Title>
        <Modal.Content>
          <span>
            You have unsaved <b>Novelty Calculation results</b>. <br />
            Leaving now means you'll lose your changes.
          </span>
        </Modal.Content>
        <Modal.Footer>
          <Button
            onClick={handleLeaveSummary}
            aria-label="Leave without saving"
            appearance={'ghost'}
            variant={'neutral'}
          >
            Leave without saving
          </Button>
          <Button
            onClick={() => setShowLeaveSummaryModal(false)}
            aria-label="Stay"
          >
            Stay on page
          </Button>
        </Modal.Footer>
      </Modal>
    </PageWrapper>
  );
};
